import { CollectionWithCountAndFeaturedSpecial } from "../../models/collections";
import { type SpecialRow } from "../../legacy/specials";
import { createSupabaseAdminClient } from "../../supabase/supabase";
import dayjs from "@punchup/utils/dayjs";
import { getCollectionIDsForSpecial } from "./collectionSpecials";
import { logException } from "@punchup/sentry";
import { fetchWithCache } from "@punchup/utils/caches/index";
import { collectionLatestEpisodeCacheKey } from "../cache/cache-helpers-server";

export async function getCollections(params: {
  comedian_id?: string;
  collection_id?: string;
  collection_slug?: string;
  with_featured_special?: boolean;
  specials_limit?: number;
  containing_special_id?: string;
  paid?: boolean;
  kind?: "collection" | "podcast";
}): Promise<CollectionWithCountAndFeaturedSpecial[]> {
  const supabase = createSupabaseAdminClient();
  const {
    comedian_id,
    collection_id,
    collection_slug,
    with_featured_special,
    specials_limit,
    containing_special_id,
    paid,
    kind,
  } = params;

  const query = supabase.from("collections").select("*");

  if (comedian_id) {
    query.eq("comedian_id", comedian_id);
  }
  if (collection_id) {
    query.eq("id", collection_id);
  }
  if (collection_slug) {
    query.eq("slug", collection_slug);
  }
  if (paid !== undefined) {
    query.eq("requires_purchase", paid);
  }
  if (kind !== undefined) {
    query.eq("kind", kind);
  }
  if (containing_special_id) {
    const ids = await getCollectionIDsForSpecial(containing_special_id);
    if (ids.length === 0) return [];
    query.in("id", ids);
  }

  const { data, error } = await query
    .lte("published_at", dayjs().toISOString())
    .order("published_at", { ascending: false });

  if (error) {
    logException(error, {
      tags: {
        ...params,
        supabase: true,
      },
    });
    throw error;
  }

  // Batch-fetch published-only counts for all returned collections in one query
  const publishedCountMap: Record<string, number> = {};
  if (data.length > 0) {
    const collectionIds = data.map((c) => c.id);
    const { data: liveRows, error: liveCountError } = await supabase
      .from("collection_specials")
      .select("collection_id, specials!inner(id)")
      .in("collection_id", collectionIds)
      .eq("specials.is_live", true);
    if (liveCountError) {
      logException(liveCountError, { tags: { supabase: true } });
      throw liveCountError;
    }
    for (const row of liveRows ?? []) {
      publishedCountMap[row.collection_id] =
        (publishedCountMap[row.collection_id] ?? 0) + 1;
    }
  }

  if (with_featured_special || specials_limit) {
    const withFeatured = await Promise.all(
      data?.map(async (collection) => {
        // Query for featured special (if needed)
        let featuredSpecialData = null;
        if (with_featured_special) {
          const { data: featuredData, error: featuredError } = await supabase
            .from("collection_specials")
            .select("specials!inner(*)")
            .eq("collection_id", collection.id)
            .eq("is_featured", true)
            .eq("specials.is_live", true)
            .limit(1);

          if (featuredError) throw new Error(featuredError.message);
          featuredSpecialData = featuredData;
        }

        // Query for specials list (sorted by sort_order)
        // If we need featured but didn't find one, fetch at least 1 special for fallback
        let specialsData = null;
        let specialsError = null;
        const needsSpecialsList =
          specials_limit ||
          (with_featured_special &&
            (!featuredSpecialData || featuredSpecialData.length === 0));

        if (needsSpecialsList) {
          const limit = specials_limit || 1; // Fetch 1 for fallback if not specified
          const result = await supabase
            .from("collection_specials")
            .select("specials!inner(*)")
            .eq("collection_id", collection.id)
            .eq("specials.is_live", true)
            .order("sort_order", { ascending: true })
            .limit(limit);
          specialsData = result.data;
          specialsError = result.error;
        }

        if (specialsError) throw new Error(specialsError.message);

        const allSpecials = specialsData?.map((cs) => cs.specials) ?? [];
        const specials = allSpecials.filter(
          (s): s is NonNullable<typeof s> => s !== null && s.is_live === true
        );

        // Get featured special from the featured query, or fall back to first special
        let featured_special = null;
        if (featuredSpecialData && featuredSpecialData.length > 0) {
          const fs = featuredSpecialData[0].specials;
          featured_special = fs !== null && fs.is_live === true ? fs : null;
        }
        if (featured_special === null && with_featured_special) {
          // Fallback to first published special if no featured special is set
          featured_special = specials[0] ?? null;
        }

        return {
          ...collection,
          specials_count: publishedCountMap[collection.id] ?? 0,
          featured_special,
          specials: specials_limit ? specials : undefined,
        };
      })
    );
    return withFeatured;
  }

  return data.map((collection) => ({
    ...collection,
    specials_count: publishedCountMap[collection.id] ?? 0,
    featured_special: null,
  }));
}

async function getLatestPodcastEpisode(
  collectionId: string
): Promise<SpecialRow | null> {
  const supabase = createSupabaseAdminClient();
  // Query from specials so ORDER BY published_at DESC LIMIT 1 applies directly
  // to the specials table. Querying from collection_specials and using
  // referencedTable orders the embedded array, not the outer rows.
  const { data, error } = await supabase
    .from("specials")
    .select("*, collection_specials!inner(collection_id)")
    .eq("collection_specials.collection_id", collectionId)
    .eq("is_live", true)
    .not("published_at", "is", null)
    .order("published_at", { ascending: false })
    .limit(1);
  if (error) {
    logException(error, { tags: { collectionId, supabase: true } });
    throw error;
  }
  if (!data?.[0]) return null;
  const { collection_specials: _, ...special } = data[0];
  return special as SpecialRow;
}

export async function getLatestPodcastEpisodeWithCache(
  collectionId: string
): Promise<SpecialRow | null> {
  const cacheKey = collectionLatestEpisodeCacheKey(collectionId);
  return fetchWithCache(
    cacheKey,
    () => getLatestPodcastEpisode(collectionId),
    300 // 5 minutes
  );
}

/**
 * Check if a comedian has any paid bundles (collections)
 */
export async function comedianHasBundles(comedianId: string): Promise<boolean> {
  try {
    const collections = await getCollections({
      comedian_id: comedianId,
      kind: "collection",
    });
    return collections.length > 0;
  } catch (error) {
    logException(error);
    return false;
  }
}
